import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../animations';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  // Ensure the page scrolls to the top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <AnimatedSection id="privacy-policy" className="bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-white">
      <div className="container mx-auto px-6 pt-19 pb-16">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="font-semibold text-cyan-500 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors duration-300 flex items-center gap-2 mb-8">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-12 text-slate-900 dark:text-white">Privacy Policy</h1>
          
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
            <div className="max-w-none text-slate-600 dark:text-gray-300">
              <p className="text-slate-500 dark:text-gray-400 mb-6">Last updated: July 18, 2025</p>

              <p className="mb-6">Smart Solutions ("us", "we", or "our") operates the https://smartsolutionslk.com website (the "Service"). This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.</p>

            <h2 className="mt-8 mb-4 text-2xl font-bold text-cyan-500 dark:text-cyan-400">Information Collection and Use</h2>
            <p className="mb-6">We collect several different types of information for various purposes to provide and improve our Service to you.</p>
            
            <h3 className="mt-6 mb-4 text-xl font-semibold text-slate-800 dark:text-gray-200">Types of Data Collected</h3>
            <h4 className="mt-4 mb-3 text-lg font-medium text-slate-700 dark:text-gray-300">Personal Data</h4>
            <p className="mb-4">While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:</p>
            <div className="bg-slate-50 dark:bg-gray-700 rounded-lg p-6 my-4">
              <ul className="space-y-2">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                  <span>Email address</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                  <span>First name and last name</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                  <span>Phone number</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                  <span>Cookies and Usage Data</span>
                </li>
              </ul>
            </div>

            <h4 className="mt-6 mb-3 text-lg font-medium text-slate-700 dark:text-gray-300">Usage Data</h4>
            <p className="mb-6">We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.</p>

            <h2 className="mt-8 mb-4 text-2xl font-bold text-cyan-500 dark:text-cyan-400">Use of Data</h2>
            <p className="mb-4">Smart Solutions uses the collected data for various purposes:</p>
            <div className="bg-slate-50 dark:bg-gray-700 rounded-lg p-6 my-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></span>
                  <span>To provide and maintain our Service</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></span>
                  <span>To notify you about changes to our Service</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></span>
                  <span>To provide customer support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></span>
                  <span>To gather analysis or valuable information so that we can improve our Service</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></span>
                  <span>To monitor the usage of our Service</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2"></span>
                  <span>To detect, prevent and address technical issues</span>
                </li>
              </ul>
            </div>

            <h2 className="mt-8 mb-4 text-2xl font-bold text-cyan-500 dark:text-cyan-400">Data Security</h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-500 p-6 my-4 rounded-r-lg">
              <p className="text-yellow-800 dark:text-yellow-300"><strong>Important Notice:</strong> The security of your data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.</p>
            </div>
            
            <h2 className="mt-8 mb-4 text-2xl font-bold text-cyan-500 dark:text-cyan-400">Your Rights</h2>
            <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-6 my-4 rounded-r-lg">
              <p className="text-blue-800 dark:text-blue-300"><strong>Your Rights:</strong> In accordance with Sri Lankan law and other applicable regulations, you have the right to access, update, or delete the information we have on you. If you wish to be informed what Personal Data we hold about you and if you want it to be removed from our systems, please contact us.</p>
            </div>
            
            <h2 className="mt-8 mb-4 text-2xl font-bold text-cyan-500 dark:text-cyan-400">Contact Us</h2>
            <div className="bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 p-6 my-4 rounded-r-lg">
              <p className="text-green-800 dark:text-green-300 mb-3"><strong>Questions about this Privacy Policy?</strong></p>
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-green-700 dark:text-green-300">By email: <a href="mailto:info@smartsolutionslk.com" className="underline hover:text-green-600 dark:hover:text-green-200">info@smartsolutionslk.com</a></span>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export default PrivacyPolicyPage;
